/**
 * Band-limited downsampler for KT1 (FR-71): device rate (typically 48 or
 * 44.1 kHz) to 16 kHz mono.
 *
 * Windowed-sinc (Blackman) low-pass evaluated at each output position, with
 * the kernel precomputed into a finely sampled lookup table. Offline (runs once
 * on stop), deterministic and runtime-neutral, so it can be unit-tested and
 * benchmarked outside the browser.
 */

/** Zero crossings of the sinc on each side, measured at the output rate. */
const HALF_ZEROS = 16
/** Cut-off as a fraction of the output Nyquist; leaves a transition band below 8 kHz. */
const CUTOFF = 0.9
/** Lookup-table resolution: kernel samples per input sample. */
const TABLE_RESOLUTION = 512

export function resample(
  input: Float32Array,
  inRate: number,
  outRate: number,
): Float32Array {
  if (inRate <= 0 || outRate <= 0) throw new RangeError('sample rates must be positive')
  if (outRate > inRate) throw new RangeError('upsampling is not supported')
  if (inRate === outRate) return input.slice()

  const ratio = inRate / outRate
  // Normalised cut-off in cycles per input sample.
  const fc = (CUTOFF * 0.5) / ratio
  const half = HALF_ZEROS * ratio // kernel half-width in input samples
  const table = buildKernelTable(fc, half)

  const outLength = Math.floor((input.length * outRate) / inRate)
  const out = new Float32Array(outLength)
  const last = input.length - 1

  for (let i = 0; i < outLength; i++) {
    const center = i * ratio
    const k0 = Math.max(0, Math.ceil(center - half))
    const k1 = Math.min(last, Math.floor(center + half))
    let acc = 0
    let norm = 0
    for (let k = k0; k <= k1; k++) {
      const pos = Math.abs(k - center) * TABLE_RESOLUTION
      const j = Math.floor(pos)
      const frac = pos - j
      const a = table[j] ?? 0
      const b = table[j + 1] ?? 0
      const h = a + (b - a) * frac
      acc += (input[k] ?? 0) * h
      norm += h
    }
    // Normalising by the taps actually used keeps unity gain at the edges.
    out[i] = norm !== 0 ? acc / norm : 0
  }
  return out
}

function buildKernelTable(fc: number, half: number): Float32Array {
  const size = Math.ceil(half * TABLE_RESOLUTION) + 2
  const table = new Float32Array(size)
  for (let j = 0; j < size; j++) {
    const d = j / TABLE_RESOLUTION
    if (d > half) break
    const x = 2 * fc * d
    const sinc = x === 0 ? 1 : Math.sin(Math.PI * x) / (Math.PI * x)
    const w = blackman(d / half)
    table[j] = 2 * fc * sinc * w
  }
  return table
}

/** Symmetric Blackman window; t in [0, 1] is distance from the centre. */
function blackman(t: number): number {
  const x = 0.5 + 0.5 * t // map centre..edge to 0.5..1 of the full window
  return 0.42 - 0.5 * Math.cos(2 * Math.PI * x) + 0.08 * Math.cos(4 * Math.PI * x)
}
